import { useRef, useEffect, useCallback, type RefObject } from 'react';

/**
 * Marks carousel items with `data-offscreen` so CSS can skip their transitions.
 * Optionally calls `onIntersectionChange` when visibility changes.
 */
export function useOffscreenObserver(
  scrollRoot: RefObject<HTMLDivElement | null>,
  onIntersectionChange?: (el: HTMLElement, isIntersecting: boolean) => void,
) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingElements = useRef<Set<HTMLElement>>(new Set());

  useEffect(() => {
    const root = scrollRoot.current;
    if (!root) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          if (entry.isIntersecting) {
            el.removeAttribute('data-offscreen');
          } else {
            el.setAttribute('data-offscreen', 'true');
          }
          onIntersectionChange?.(el, entry.isIntersecting);
        }
      },
      { root, rootMargin: '0px 50px 0px 50px' },
    );

    pendingElements.current.forEach((el) => observerRef.current?.observe(el));

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const register = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    pendingElements.current.add(el);
    observerRef.current?.observe(el);
  }, []);

  return register;
}
